/*
 * tp-addon-pills.js
 *
 * Companion to snippets/tp-addon-pills.liquid.
 *
 * 1) Add-on adds: listens for the theme's `product-ajax:added` event
 *    (dispatched by ProductForm in theme.js after ANY successful add — main
 *    buy buttons and the sticky ATC bar both use ProductForm). When the added
 *    product matches this page's main product and pills are selected, the
 *    selected add-on variants are added to the cart with a second /cart/add
 *    call, and the cart drawer is refreshed the same way the theme's own
 *    bundle flow does it (cart:grouped-sections + pubsub cartUpdate).
 *
 * 2) Price feedback: while pills are selected, the Add to cart label shows
 *    the combined total ("Add to cart · $35"), recalculated on pill toggles
 *    and on variant/license changes (via the theme's variantChange pubsub).
 *
 * Deliberately NOT wired into the product form itself: if this script fails,
 * Add to cart behaves exactly as stock.
 */
if (!customElements.get('tp-addon-pills')) {
  customElements.define(
    'tp-addon-pills',
    class TpAddonPills extends HTMLElement {
      connectedCallback() {
        this.mainProductId = Number(this.dataset.productId);
        this.sectionId = this.dataset.sectionId;
        this.currentPriceCents = Number(this.dataset.basePrice) || 0;
        this.isAdding = false;

        this.pills = Array.from(this.querySelectorAll('[data-tp-addon-pill]'));
        this.pills.forEach((pill) => {
          const input = pill.querySelector('input[type="checkbox"]');
          if (!input) return;
          input.addEventListener('change', () => {
            pill.classList.toggle('active', input.checked);
            this.updateAtcLabel();
          });
        });

        this.onProductAdded = this.onProductAdded.bind(this);
        document.addEventListener('product-ajax:added', this.onProductAdded);

        // Track variant/license changes so the total stays correct. The
        // pubsub payload carries the section id, so quick-view popups of
        // other products can't pollute our price.
        if (window.FoxTheme && FoxTheme.pubsub) {
          this.variantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
            (event) => {
              const data = event.data || {};
              if (data.sectionId !== this.sectionId) return;
              if (data.variant && typeof data.variant.price === 'number') {
                this.currentPriceCents = data.variant.price;
              }
              // The theme may have rewritten the button label (e.g. sold out
              // state), which destroys our suffix — re-apply it.
              this.updateAtcLabel();
            }
          );
        }
      }

      disconnectedCallback() {
        document.removeEventListener('product-ajax:added', this.onProductAdded);
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
      }

      get selectedInputs() {
        return this.pills
          .map((pill) => pill.querySelector('input[type="checkbox"]'))
          .filter((input) => input && input.checked);
      }

      get selectedVariantIds() {
        return this.selectedInputs.map((input) => Number(input.dataset.addonVariantId)).filter((id) => id > 0);
      }

      get selectedAddonTotalCents() {
        return this.selectedInputs.reduce((sum, input) => sum + (Number(input.dataset.addonPrice) || 0), 0);
      }

      get atcButton() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const noDecimals = /no_decimals/.test(format);
        const amount = noDecimals
          ? Math.round(cents / 100).toString()
          : (cents / 100).toFixed(2).replace(/\.00$/, '');
        return format.replace(/\{\{[^}]*\}\}/, amount).replace(/<[^>]*>/g, '');
      }

      updateAtcLabel() {
        const button = this.atcButton;
        if (!button) return;
        const host = button.querySelector('span');
        if (!host) return;

        let suffix = host.querySelector('.tp-atc-total');
        const addonTotal = this.selectedAddonTotalCents;

        if (!addonTotal || button.disabled) {
          if (suffix) suffix.remove();
          return;
        }

        if (!suffix) {
          suffix = document.createElement('span');
          suffix.className = 'tp-atc-total';
          host.appendChild(suffix);
        }
        suffix.textContent = ` · ${this.formatMoney(this.currentPriceCents + addonTotal)}`;
      }

      onProductAdded(event) {
        if (!window.FoxTheme) return;

        const added = (event.detail && event.detail.product) || {};
        // /cart/add responds with a single line item ({product_id: ...}) for
        // form adds, or {items: [...]} for multi-item adds.
        let addedProductIds = [];
        if (Array.isArray(added.items)) {
          addedProductIds = added.items.map((item) => Number(item.product_id));
        } else if (added.product_id) {
          addedProductIds = [Number(added.product_id)];
        }
        if (!addedProductIds.includes(this.mainProductId)) return;

        const variantIds = this.selectedVariantIds;
        if (!variantIds.length || this.isAdding) return;

        this.addAddonsToCart(variantIds);
      }

      async addAddonsToCart(variantIds) {
        this.isAdding = true;
        try {
          const sectionsToBundle = [];
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
          );

          const body = JSON.stringify({
            items: variantIds.map((id) => ({ id, quantity: 1 })),
            sections: sectionsToBundle,
            sections_url: window.location.pathname,
          });

          const response = await fetch(`${FoxTheme.routes.cart_add_url}`, {
            ...FoxTheme.utils.fetchConfig('javascript'),
            ...{ body },
          });
          const parsedState = await response.json();

          if (parsedState.status) {
            console.warn('[tp-addon-pills] add-on add failed:', parsedState.description || parsedState.message);
            return;
          }

          const cartJson = await (
            await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })
          ).json();
          cartJson['sections'] = parsedState['sections'];

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });

          this.reset();
        } catch (error) {
          console.warn('[tp-addon-pills] error adding add-ons:', error);
        } finally {
          this.isAdding = false;
        }
      }

      reset() {
        this.pills.forEach((pill) => {
          const input = pill.querySelector('input[type="checkbox"]');
          if (input) input.checked = false;
          pill.classList.remove('active');
        });
        this.updateAtcLabel();
      }
    }
  );
}
